import React, {useState} from 'react';
import {observer} from "mobx-react-lite";
import {ellipsisVertical} from "ionicons/icons";
import {ActionSheetButton, IonIcon} from "@ionic/react";
import styled from "styled-components";
import {useServices} from "../../hooks/use-services.hook";
import {SwitchEnvironmentDialogComponent} from "./switch-environment-dialog.component";
import {DialogCloseButtonBehavior} from "../../services/dialog/dialog-enums";
import {ActionsSheetComponent} from "../action-sheet/action-sheet.component";
import {ApplicationEnvironmentTypeEnum} from "../../services/configuration/configuration-object.interface";
import {IServiceFactory} from "../../services/service-factory.interface";

const TestModeMenuBox = styled.div<{color?: string}>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    height: 26px;
    font-size: ${props => props.theme.fontSize.h2};
    cursor: pointer;
    color: ${props => props.color ?? "inherit"};
`


class TestModeMenuGlobals {
    static isHiddenOnCurrentSession = false;
}

export function shouldRenderTestModeMenu(services: IServiceFactory): boolean {
    if(services.device.isHybrid) {
        return services.configuration.isHybridTestModeEnabled;
    }

    return !services.configuration.isProduction;
}

export const TestModeMenuComponent: React.FC = observer(() => {
    const services = useServices();
    const [isOpen, setIsOpen] = useState(false);
    const [isHidden, setIsHidden] = useState(TestModeMenuGlobals.isHiddenOnCurrentSession);

    const shouldRender = () => {
        if(isHidden) {
            return false;
        }

        return shouldRenderTestModeMenu(services);
    }


    if(!shouldRender()) {
        return null;
    }

    const switchLayoutModeHandler = () => {
        services.layout.switchLayoutMode();
    }

    const switchPortalType = async () => {
        await services.configuration.switchPortalType();
    }

    const switchEnvironmentHandler = async () => {
        await services.dialog.showPopupDialog({
            closeButtonBehavior: DialogCloseButtonBehavior.Reject,
            render: dialogHandler => (<SwitchEnvironmentDialogComponent dialogHandler={dialogHandler}/>)
        })
    }

    const buttons: ActionSheetButton[] = []

    if(services.device.isWeb && !services.configuration.isAgencyPortal) {
        buttons.push( {
            text: services.layout.shouldUseWebappLayout ? "Switch to mobile app layout" : "Switch to web app layout",
            handler: switchLayoutModeHandler
        });
    }

    if(services.configuration.currentEnvironmentType === ApplicationEnvironmentTypeEnum.Development
        && services.navigator.currentRoute.isHome
        && services.layout.shouldUseWebappLayout) {
        buttons.push( {
            text: services.configuration.isAgencyPortal ? "Switch to B2C" : "Switch to Agency",
            handler: switchPortalType
        });
    }

    if(services.device.isHybrid && services.navigator.currentRoute.isHome) {
        buttons.push({
            text: "Switch environment",
            handler: switchEnvironmentHandler
        });
    }

    if(buttons.length > 0) {
        buttons.push( {
            text: 'Hide this menu',
            handler: () => {
                TestModeMenuGlobals.isHiddenOnCurrentSession = true;
                setIsHidden(TestModeMenuGlobals.isHiddenOnCurrentSession);
            }
        });
    }

    if(buttons.length === 0) {
        return null;
    }

    const onClickHandler = () => {
        setIsOpen(true);
    }

    return (
        <TestModeMenuBox onClick={onClickHandler} color={!services.configuration.isProduction ? services.theme.currentTheme.colors.danger : undefined}>
            <IonIcon icon={ellipsisVertical}/>
            <ActionsSheetComponent mode={"ios"} isOpen={isOpen} buttons={buttons} onDidDismiss={() => setIsOpen(false)}/>
        </TestModeMenuBox>
    )
});
